import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';

// components
import {
  Element,
  FontType,
  Color,
  Breakpoint,
  FlipContent,
  useFlip,
  TogglePosition,
  Typography,
  Icon,
  Size
} from '@americanexpress/aqx-components';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import BackWithBottomLogo from './BackBottomLogoNoTitle';
import FrontWithLargeLogo from './FrontWithLargeLogo';
import PlatFront from './PlatFront';
import G157Carousel from 'src/universal/components/Experiments/G157/BusinessValueTilesCarousel';

// selectors
import { getData } from './redux';
import { isG157Variant } from 'src/universal/components/Experiments/G157/redux';

//utils
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { useBreakpoint } from '@americanexpress/aqx-components';

import styles from './__styles__/index.scss?modules';

const Front = ({
  header,
  text,
  logo,
  iconAsLogo,
  logoAlt,
  subHeader,
  tag,
  bottomLabel
}) => (
  <Element className={clsx(styles.contentContainer, styles.front)}>
    <Typography
      Component="h3"
      $size="15px"
      $lineHeight="22px"
      $family={FontType.HelveticaBold}
      theme={{ color: Color.DeepBlue, marginBottom: 5 }}
      className={styles.header}
    >
      {header}
    </Typography>
    <Typography
      $size="24px"
      $lineHeight="32px"
      $family={FontType.BentonMedium}
      theme={{
        color: Color.Black,
        marginBottom: 10
      }}
      className={styles.subHeader}
      Component="h3"
    >
      {subHeader}
      {tag && (
        <Typography
          Component="span"
          className={styles.tag}
          $size="10px"
          $lineHeight="120%"
          $family={FontType.BentonBold}
          theme={{ color: Color.White }}
        >
          {tag}
        </Typography>
      )}
    </Typography>
    <Typography
      $size="15px"
      $lineHeight="22px"
      $family={FontType.HelveticaRegular}
      theme={{
        color: Color.Gray06
      }}
    >
      {text}
    </Typography>
    <Element className={styles.logoContainer}>
      {logo && (
        <Element
          Component="img"
          src={logo}
          className={styles.frontLogo}
          alt={logoAlt}
        />
      )}
      {iconAsLogo && (
        <Icon
          className={styles.iconAsLogo}
          directory="dls"
          $size={Size.LG}
          imageName={iconAsLogo}
          theme={{
            color: Color.BrightBlue
          }}
        />
      )}
    </Element>
    {bottomLabel && (
      <Typography
        className={styles.bottomLabel}
        Component="div"
        $size="15px"
        $lineHeight="120%"
        $family={FontType.HelveticaRegular}
        theme={{
          color: Color.Gray05
        }}
      >
        {bottomLabel}
      </Typography>
    )}
  </Element>
);

const Back = ({ logo, iconAsLogo, logoAlt, title, flagText, disclaimer }) => (
  <Element className={clsx(styles.contentContainer, styles.back)}>
    {logo && (
      <Element theme={{ marginBottom: 5 }}>
        <Element Component="img" src={logo} alt={logoAlt} />
      </Element>
    )}
    {iconAsLogo && (
      <Icon
        className={styles.iconAsLogo}
        directory="dls"
        $size={Size.LG}
        imageName={iconAsLogo}
        theme={{
          color: Color.BrightBlue
        }}
      />
    )}
    <Typography
      Component="h3"
      $size="20px"
      $lineHeight="28px"
      $family={FontType.BentonMedium}
      theme={{ color: Color.Black, marginBottom: 10 }}
      className={styles.subHeader}
    >
      {title}
    </Typography>
    <Element>
      <Typography
        $size="15px"
        $lineHeight="22px"
        $family={FontType.HelveticaRegular}
        theme={{ color: Color.Gray06 }}
        className={styles.backText}
      >
        {flagText}
      </Typography>
      {disclaimer && (
        <Typography
          $size="13px"
          $lineHeight="20px"
          $family={FontType.HelveticaRegular}
          theme={{ color: Color.Gray05 }}
          className={clsx(styles.backText, styles.disclaimer)}
        >
          {disclaimer}
        </Typography>
      )}
    </Element>
  </Element>
);

const Tile = ({ content, tileFrontView, index, shortName }) => {
  const { isFlipped, setIsFlipped, rotation, setRotation } = useFlip();

  const dtmObj = parseArrayToObject(content.dtmEvents, 'tag');

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    setRotation(r => r + 180);
    if (!isFlipped) {
      sendDTMClickEvent(dtmObj.clickToBack);
    } else {
      sendDTMClickEvent(dtmObj.clickToFront);
    }
  };

  const { header, headerMobile, logo, logoMobile } = content;
  const customProps = {
    ...content,
    header: headerMobile ? headerMobile : header,
    logo: logoMobile ? logoMobile : logo
  };

  let cardFrontInstance;

  if (tileFrontView.isPlatinumMobile) {
    cardFrontInstance = <PlatFront {...customProps} />;
  } else if (tileFrontView.isGoldMobile) {
    cardFrontInstance = <FrontWithLargeLogo {...customProps} />;
  } else {
    cardFrontInstance = <Front {...content} />;
  }

  return (
    <Element className={clsx(styles.flipCardContainer, styles[`tile${index}`])}>
      <FlipContent
        isFlipped={isFlipped}
        handleFlip={handleFlip}
        rotation={rotation}
        togglePosition={TogglePosition.BottomRight}
        ariaLabelFront={content.frontAriaLabel}
        ariaLabelBack={content.backAriaLabel}
        cardStyles={{ back: { display: 'flex' }, front: { display: 'flex' } }}
      >
        {cardFrontInstance}

        {shortName === 'gold' ? (
          <BackWithBottomLogo {...content} />
        ) : (
          <Back {...content} />
        )}
      </FlipContent>
    </Element>
  );
};

const Tiles = ({ data, isG157 }) => {
  const { sm: isMobile } = useBreakpoint();
  const { shouldRender, content, header, subHeader, dtm, shortName } = data;

  if (!shouldRender) {
    return null;
  }

  const impressionDtm = {
    ...dtm,
    eventAsset: `${dtm?.eventAsset}_${shortName}`
  };

  if (isG157 && isMobile) {
    const isPlatM = shortName === 'platinum';
    const isGoldM = shortName === 'gold';
    const itemHeaderList = [];
    const slideList = content.map((item, i) => {
      itemHeaderList.push(item.headerMobile ? item.headerMobile : item.header);
      return (
        <Tile
          key={i}
          content={item}
          tileFrontView={{ isPlatinumMobile: isPlatM, isGoldMobile: isGoldM }}
          index={i}
          shortName={shortName}
        />
      );
    });

    return (
      <div
        id="gcsPdpValueTiles"
        className={clsx(styles.valueTilesWrapper, styles[shortName], styles.isG157)}
      >
        <G157Carousel
          slideList={slideList}
          header={header}
          subHeader={subHeader}
          needHeaderBox={false}
          originKey="bizValueTiles"
          itemHeaderList={itemHeaderList}
        />
      </div>
    );
  }

  return (
    <Breakpoint>
      {({ sm }) => {
        const isPlatinumMobile = shortName === 'platinum' && sm;
        const isGoldMobile = shortName === 'gold' && sm;

        return (
          <Fragment>
            <Element
              id="gcsPdpValueTiles"
              className={clsx(styles.valueTilesWrapper, styles[shortName])}
              theme={{ marginBottom: 30 }}
            >
              <LazyScrollMetricTracker scrollDtm={impressionDtm} />
              <Typography
                Component="h2"
                className={styles.sectionTitle}
                theme={{ marginBottom: isPlatinumMobile ? 15 : 20 }}
              >
                {header}
              </Typography>
              {isPlatinumMobile &&
                subHeader && (
                  <Typography
                    $family={FontType.HelveticaRegular}
                    $size={15}
                    $lineHeight={22}
                    className={styles.variantSubHeader}
                  >
                    {subHeader}
                  </Typography>
                )}
              <Element className={styles.flipCardsContainer}>
                {content.map((item, i) => (
                  <Tile
                    key={i}
                    content={item}
                    tileFrontView={{ isPlatinumMobile, isGoldMobile }}
                    index={i}
                    shortName={shortName}
                  />
                ))}
              </Element>
            </Element>
          </Fragment>
        );
      }}
    </Breakpoint>
  );
};

const mapStateToProps = state => ({
  data: getData(state),
  isG157: isG157Variant(state)
});

export default connect(mapStateToProps)(Tiles);
